function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
import { CONNECT_ELEMENTS_OVERLAY_ZINDEX_DEFAULT } from './appearanceOptionsDefaults';
import { fontSizeKeywordRegex, fontSizeRegex, hslRegex, hslaRegex, hslaSlashRegex, rgbRegex, rgbaRegex, rgbaSlashRegex, sixHexRegex, threeHexRegex } from './constants';
import { fontWeightMap, getTokenNumericValue, isSupportedFontSizeBaseUnit, numericOptionLimits, fontSizeOptionLimits, parseFontSizeUnit, parseFontWeight } from './tokenUtils';
export const validateObject = (logWarning, expectedMembers, optional, shouldThrow)=>(value, path)=>{
        if (optional && value == null) {
            return;
        }
        if (typeof value !== 'object') {
            throw new Error(`${path} must be an object`);
        }
        if (!value) {
            throw new Error(`${path} cannot be null`);
        }
        Object.keys(value).forEach((key)=>{
            if (!expectedMembers.hasOwnProperty(key)) {
                if (shouldThrow) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    delete value[key];
                    throw new Error(`${path}.${key} is not an expected option`);
                } else {
                    logWarning(`${path}.${key} is not an expected option`);
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    delete value[key];
                }
            }
        });
        Object.keys(expectedMembers).forEach((key)=>{
            try {
                expectedMembers[key](// eslint-disable-next-line @typescript-eslint/no-explicit-any
                value[key], `${path}.${key}`);
            } catch (error) {
                if (shouldThrow) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    delete value[key];
                    throw error;
                }
                logWarning(error.message);
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                delete value[key];
            }
        });
    };
export const validateFunction = (value, path)=>{
    if (typeof value !== 'function') {
        throw new Error(`${path} was expected to be a function, but we got '${typeof value}'.`);
    }
};
export const validateArray = (logWarning, validator)=>(value, path)=>{
        if (!Array.isArray(value)) {
            throw new Error(`${path} must be an array`);
        }
        let i = 0;
        while(i < value.length){
            try {
                validator(value[i], `${path}[${i}]`);
                i += 1;
            } catch (error) {
                logWarning(error.message);
                value.splice(i, 1);
            }
        }
    };
export const validateStringBooleanRecord = (value, path)=>{
    if (!value || typeof value !== 'object') {
        throw new Error(`${path} must be an object`);
    }
    const entries = Object.entries(value);
    // eslint-disable-next-line no-restricted-syntax
    for (const key of entries){
        if (typeof key[1] !== 'boolean') {
            throw new Error(`${path} contains an key that does not have a boolean entry: ${key[0]}`);
        }
    }
};
export const validateStringStringRecord = (value, path)=>{
    if (!value || typeof value !== 'object') {
        throw new Error(`${path} must be an object`);
    }
    const entries = Object.entries(value);
    // eslint-disable-next-line no-restricted-syntax
    for (const key of entries){
        if (typeof key[1] !== 'string') {
            throw new Error(`${path} contains an key that does not have a string entry: ${key[0]}`);
        }
    }
};
export const validateString = (value, path)=>{
    if (typeof value !== 'string') {
        throw new Error(`${path} must be a string`);
    }
};
export const validateNumber = (value, path)=>{
    if (typeof value !== 'number') {
        throw new Error(`${path} must be a number`);
    }
    if (isNaN(value)) {
        throw new Error(`${path} must be a valid number`);
    }
};
export const allowedTextTransformValues = [
    'uppercase',
    'lowercase',
    'capitalize',
    'none'
];
export const isValidTextTransform = (textTransformInput)=>{
    const textTransform = textTransformInput === null || textTransformInput === void 0 ? void 0 : textTransformInput.toLowerCase();
    // An empty value is acceptable
    if (!textTransform) {
        return true;
    }
    return allowedTextTransformValues.includes(textTransform);
};
export const validateTextTransform = (value, path)=>{
    if (typeof value !== 'string') {
        throw new Error(`${path} must be a string`);
    }
    if (!isValidTextTransform(value)) {
        throw new Error(`${path} specifies disallowed value ${value}, must be one of ${allowedTextTransformValues.join(', ')}`);
    }
};
export const validateBoolean = (value, path)=>{
    if (typeof value !== 'boolean') {
        throw new Error(`${path} must be a boolean`);
    }
};
export const ignoreNullish = (validator)=>(value, path)=>{
        if (value == null) {
            return;
        }
        validator(value, path);
    };
export const validateNotNullish = (missingMessage)=>(value, _path)=>{
        if (!value) {
            throw new Error(missingMessage);
        }
    };
export const validateStringContents = (callback)=>(value, path)=>{
        validateString(value, path);
        callback(value, path);
    };
export const validateNumberContents = (callback)=>(value, path)=>{
        validateNumber(value, path);
        callback(value, path);
    };
export const composeValidations = (...validations)=>(value, path)=>{
        // eslint-disable-next-line no-restricted-syntax
        for (const validator of validations){
            validator(value, path);
        }
    };
// TODO: Handle multiple unit types/handle units dynamically like how parseFontSizeUnit works
// This function should be able to handle <length> and <percentage> values
// https://developer.mozilla.org/en-US/docs/Web/CSS/border-radius#formal_syntax
const tokenUnit = 'px';
const regEx = new RegExp(`^[0-9]+${tokenUnit}$`);
export const isValidRadiusString = (radiusInput)=>{
    const radius = radiusInput === null || radiusInput === void 0 ? void 0 : radiusInput.toLowerCase();
    if (!radius || !regEx.test(radius)) {
        return false;
    }
    const radiusNumber = getTokenNumericValue(radius, 'px');
    if (radiusNumber < numericOptionLimits.radius.min || radiusNumber > numericOptionLimits.radius.max) {
        return false;
    }
    return true;
};
export const isValidSpacingString = (spacingInput)=>{
    const spacing = spacingInput === null || spacingInput === void 0 ? void 0 : spacingInput.toLowerCase();
    if (!spacing || !regEx.test(spacing)) {
        return false;
    }
    // Currently spacing is only supported in px
    const spacingNumber = getTokenNumericValue(spacing, 'px');
    if (spacingNumber < numericOptionLimits.spacing.min || spacingNumber > numericOptionLimits.spacing.max) {
        return false;
    }
    return true;
};
export const isValidFontWeightString = (fontWeightInput)=>{
    const fontWeight = fontWeightInput === null || fontWeightInput === void 0 ? void 0 : fontWeightInput.toLowerCase();
    if (!fontWeight) {
        return false;
    }
    const fontWeightNumber = parseFontWeight(fontWeight);
    if (!fontWeightNumber) {
        return false;
    }
    if (fontWeightNumber < numericOptionLimits.fontWeight.min || fontWeightNumber > numericOptionLimits.fontWeight.max) {
        return false;
    }
    return true;
};
// only px, em, rem are supported for font size base units.
export const isValidFontSizeBaseString = (fontSizeBaseInput)=>{
    const fontSizeBase = fontSizeBaseInput === null || fontSizeBaseInput === void 0 ? void 0 : fontSizeBaseInput.toLowerCase();
    if (!fontSizeBase) {
        return false;
    }
    const fontUnit = parseFontSizeUnit(fontSizeBase);
    if (typeof fontUnit !== 'string') {
        return false;
    }
    if (!isSupportedFontSizeBaseUnit(fontUnit)) {
        return false;
    }
    let fontUnitLookup = 'px';
    if (fontUnit === 'px' || fontUnit === 'em' || fontUnit === 'rem') {
        fontUnitLookup = fontUnit;
    }
    // Currently font size base is only supported in px
    const fontSizeBaseNumber = getTokenNumericValue(fontSizeBase, fontUnit);
    if (fontSizeBaseNumber < fontSizeOptionLimits.fontSizeBase[fontUnitLookup].min || fontSizeBaseNumber > fontSizeOptionLimits.fontSizeBase[fontUnitLookup].max) {
        return false;
    }
    return true;
};
const isValidFontSizeNumber = (fontSize)=>{
    if (fontSizeRegex.test(fontSize)) {
        const fontUnit = parseFontSizeUnit(fontSize);
        if (!fontUnit) {
            return false;
        }
        const fontSizeNumber = getTokenNumericValue(fontSize, fontUnit);
        let fontUnitLookup = 'px';
        if (fontUnit === 'px' || fontUnit === 'em' || fontUnit === 'rem') {
            fontUnitLookup = fontUnit;
            if (fontSizeNumber < fontSizeOptionLimits.fontSize[fontUnitLookup].min || fontSizeNumber > fontSizeOptionLimits.fontSize[fontUnitLookup].max) {
                return false;
            }
        } else if (fontSizeNumber < fontSizeOptionLimits.fontSize.other.min || fontSizeNumber > fontSizeOptionLimits.fontSize.other.max) {
            return false;
        }
        return true;
    }
    return false;
};
export const isValidFontSizeString = (fontSizeInput)=>{
    const fontSize = fontSizeInput === null || fontSizeInput === void 0 ? void 0 : fontSizeInput.toLowerCase();
    if (!fontSize) {
        return false;
    }
    if (fontSizeKeywordRegex.test(fontSize)) {
        return true;
    }
    // If the font size is not a keyword, it must be a number
    return isValidFontSizeNumber(fontSize);
};
export const isValidColorString = (colorInput, allowTransparency)=>{
    const color = colorInput.trim().toLowerCase();
    if (threeHexRegex.test(color)) {
        return true;
    }
    if (sixHexRegex.test(color)) {
        return true;
    }
    // rgb(12, 23, 34)
    if (rgbRegex.test(color)) {
        return true;
    }
    // hsl(12, 23%, 34%)
    if (hslRegex.test(color)) {
        return true;
    }
    if (allowTransparency) {
        // rgba(12, 23, 34, 0.45)
        // rgba(12, 23, 34, .45)
        // rgba(12, 23, 34, 45%)
        if (rgbaRegex.test(color)) {
            return true;
        }
        // rgba(12, 23, 34 / 0.45)
        // rgba(12, 23, 34 / .45)
        // rgba(12, 23, 34 / 45%)
        if (rgbaSlashRegex.test(color)) {
            return true;
        }
        // hsla(12, 23, 34, 0.45)
        // hsla(12, 23, 34, .45)
        if (hslaRegex.test(color)) {
            return true;
        }
        // hsla(12, 23, 34 / 0.45)
        // hsla(12, 23, 34 / .45)
        if (hslaSlashRegex.test(color)) {
            return true;
        }
    }
    return false;
};
const formatArrayOfExpectedValues = (acceptableValues)=>{
    return acceptableValues.reduce((prev, cur, idx)=>{
        if (idx === 0) {
            return `'${cur}'`;
        } else if (idx === acceptableValues.length - 1) {
            return `${prev}, or '${cur}'`;
        } else {
            return `${prev}, '${cur}'`;
        }
    }, '');
};
const globalValues = [
    'inherit',
    'initial',
    'revert',
    'revert-layer',
    'unset'
];
export const textDecorationStyleKeywords = [
    'solid',
    'double',
    'dotted',
    'dashed',
    'wavy'
];
// acceptable values referring to https://developer.mozilla.org/docs/Web/CSS/text-decoration-style
export const allowedTextDecorationStyleValues = [
    ...textDecorationStyleKeywords,
    ...globalValues
];
export const validateTextDecorationStyle = validateStringContents((style, path)=>{
    if (allowedTextDecorationStyleValues.map((s)=>s.toString()).includes(style.toLowerCase())) {
        return;
    }
    throw new Error(`${path} was expected to be any of ${formatArrayOfExpectedValues(allowedTextDecorationStyleValues)}, but we got \`${style}\``);
});
export const textDecorationLineKeywords = [
    'none',
    'underline',
    'overline',
    'line-through'
];
// acceptable values referring to https://developer.mozilla.org/docs/Web/CSS/text-decoration-line
export const allowedTextDecorationLineValues = [
    ...textDecorationLineKeywords,
    ...globalValues
];
export const validateTextDecorationLine = validateStringContents((line, path)=>{
    if (allowedTextDecorationLineValues.includes(line.toLowerCase())) {
        return;
    }
    throw new Error(`${path} was expected to be any of ${formatArrayOfExpectedValues(allowedTextDecorationLineValues)}, but we got \`${line}\``);
});
export const validateTextDecorationThickness = validateStringContents((thickness, path)=>{
    const value = thickness.toLowerCase();
    // acceptable values referring to https://developer.mozilla.org/docs/Web/CSS/text-decoration-thickness
    const acceptableValues = [
        'auto',
        'from-font'
    ];
    if (acceptableValues.includes(value)) {
        return;
    }
    if (isValidFontSizeNumber(value)) {
        return;
    }
    throw new Error(`${path} was expected to be a text-decoration-thickness value, but we got \`${value}\`. Thickness values can be expressed as a length (e.g., 12px) or a percentage (e.g., 10%).`);
});
export const validateRadius = validateStringContents((radius, path)=>{
    if (isValidRadiusString(radius)) {
        return;
    }
    throw new Error(`${path} was expected to be a radius value, but we got \`${radius}\`. Radius values can be expressed as as a length (e.g., 12px). The highest unit supported is 24px to ensure UI consistency.`);
});
export const validateFontSizeBase = validateStringContents((fontSizeBase, path)=>{
    if (isValidFontSizeBaseString(fontSizeBase)) {
        return;
    }
    throw new Error(`${path} was expected to be a font size base value, but we got \`${fontSizeBase}\`. Font size base values can be expressed as a pixel value between ${fontSizeOptionLimits.fontSizeBase.px.min}px - ${fontSizeOptionLimits.fontSizeBase.px.max}px, or an em/rem value between ${fontSizeOptionLimits.fontSizeBase.rem.min} - ${fontSizeOptionLimits.fontSizeBase.rem.max}.`);
});
export const validateSpacing = validateStringContents((spacing, path)=>{
    if (isValidSpacingString(spacing)) {
        return;
    }
    throw new Error(`${path} was expected to be a spacing scale base value, but we got \`${spacing}\`. Spacing scale values can be expressed as a pixel value between ${numericOptionLimits.spacing.min}px - ${numericOptionLimits.spacing.max}px.`);
});
export const fontWeightMapKeys = Object.keys(fontWeightMap).join(', ');
export const validateFontWeight = validateStringContents((fontWeight, path)=>{
    if (isValidFontWeightString(fontWeight)) {
        return;
    }
    throw new Error(`${path} was expected to be a font weight value, but we got \`${fontWeight}\`. Font weights can be expressed as a integer between 100-1000 or a keyword (${fontWeightMapKeys}).`);
});
export const validateFontSize = validateStringContents((fontSize, path)=>{
    if (isValidFontSizeString(fontSize)) {
        return;
    }
    throw new Error(`${path} was expected to be a font size value, but we got \`${fontSize}\`. Font sizes can be expressed as a length (e.g., 12px), a percentage (e.g., 120%), or a keyword (e.g., medium).`);
});
const validateColor = (allowTransparency)=>{
    return validateStringContents((color, path)=>{
        if (isValidColorString(color, allowTransparency)) {
            return;
        }
        if (allowTransparency) {
            throw new Error(`${path} was expected to be a valid color value, but we got \`${color}\`. The ${path} color can be expressed as a three or six-digit hex value (e.g., #246 or #224466), an rgb() or rgba() function (e.g., rgb(11, 22, 33)), or hsl() or hsla() function (e.g., hsl(10, 20%, 30%)).`);
        } else {
            throw new Error(`${path} was expected to be a valid color value, but we got \`${color}\`. The ${path} color can be expressed as a three or six-digit hex value (e.g., #246 or #224466), an rgb() function (e.g., rgb(11, 22, 33)), or hsl() function (e.g., hsl(10, 20%, 30%)).`);
        }
    });
};
export const validateColorWithTransparency = validateColor(true);
export const validateColorWithoutTransparency = validateColor(false);
export const isValidZIndex = (zIndex)=>{
    return zIndex <= CONNECT_ELEMENTS_OVERLAY_ZINDEX_DEFAULT && zIndex >= -CONNECT_ELEMENTS_OVERLAY_ZINDEX_DEFAULT;
};
export const validateZIndex = validateNumberContents((value, path)=>{
    if (isValidZIndex(value)) {
        return;
    }
    throw new Error(`${path} was expected to be a zIndex, but we got \`${value}\`. The maximum range for the overlay z-index is ±${CONNECT_ELEMENTS_OVERLAY_ZINDEX_DEFAULT}.`);
});
export function validateSafeStringWithRegexp(regexp, value, path) {
    if (typeof value === 'string') {
        if (!value.match(regexp)) {
            throw new Error(`${path} contains invalid characters`);
        }
    } else {
        throw new Error(`${path} must be a string`);
    }
}
// We allow letters, numbers, whitespace, dash and double and single quotes.
// In theory more characters would be allowed by the browser.
export function validateFontFamily(value, path) {
    /*
    The following regex has two main capture groups:
    1. The first capture group validates the first font in the font family input.
    2. The second capture group validates the rest of the fonts in the font family input, ensuring they are separated by a comma and optional whitespace.
    Modifications to the first capture group should be repeated for the second capture group.
  */ validateSafeStringWithRegexp(/^([-_a-zA-Z0-9\s]*|'[-_a-zA-Z0-9()\s]*'|"[-_a-zA-Z0-9()\s]*")(,\s?[-_a-zA-Z0-9\s]+|,\s?'[-_a-zA-Z0-9()\s]+'|,\s?"[-_a-zA-Z0-9()\s]+")*$/, value, path);
}
export const validatorsForAppearanceOptions = {
    colorPrimary: ignoreNullish(validateColorWithoutTransparency),
    fontFamily: ignoreNullish(validateFontFamily),
    buttonPrimaryColorBorder: ignoreNullish(validateColorWithTransparency),
    buttonPrimaryColorBackground: ignoreNullish(validateColorWithoutTransparency),
    buttonPrimaryColorText: ignoreNullish(validateColorWithoutTransparency),
    buttonSecondaryColorBorder: ignoreNullish(validateColorWithTransparency),
    buttonSecondaryColorBackground: ignoreNullish(validateColorWithoutTransparency),
    buttonSecondaryColorText: ignoreNullish(validateColorWithoutTransparency),
    actionPrimaryColorText: ignoreNullish(validateColorWithoutTransparency),
    actionPrimaryTextDecorationColor: ignoreNullish(validateColorWithoutTransparency),
    actionPrimaryTextDecorationLine: ignoreNullish(validateTextDecorationLine),
    actionPrimaryTextDecorationStyle: ignoreNullish(validateTextDecorationStyle),
    actionPrimaryTextDecorationThickness: ignoreNullish(validateTextDecorationThickness),
    actionSecondaryColorText: ignoreNullish(validateColorWithTransparency),
    actionSecondaryTextDecorationColor: ignoreNullish(validateColorWithoutTransparency),
    actionSecondaryTextDecorationLine: ignoreNullish(validateTextDecorationLine),
    actionSecondaryTextDecorationStyle: ignoreNullish(validateTextDecorationStyle),
    actionSecondaryTextDecorationThickness: ignoreNullish(validateTextDecorationThickness),
    colorBorder: ignoreNullish(validateColorWithTransparency),
    formHighlightColorBorder: ignoreNullish(validateColorWithTransparency),
    formAccentColor: ignoreNullish(validateColorWithoutTransparency),
    colorText: ignoreNullish(validateColorWithoutTransparency),
    colorSecondaryText: ignoreNullish(validateColorWithTransparency),
    colorBackground: ignoreNullish(validateColorWithoutTransparency),
    offsetBackgroundColor: ignoreNullish(validateColorWithoutTransparency),
    formBackgroundColor: ignoreNullish(validateColorWithoutTransparency),
    colorDanger: ignoreNullish(validateColorWithoutTransparency),
    badgeNeutralColorBorder: ignoreNullish(validateColorWithTransparency),
    badgeNeutralColorBackground: ignoreNullish(validateColorWithoutTransparency),
    badgeNeutralColorText: ignoreNullish(validateColorWithoutTransparency),
    badgeSuccessColorBorder: ignoreNullish(validateColorWithTransparency),
    badgeSuccessColorBackground: ignoreNullish(validateColorWithoutTransparency),
    badgeSuccessColorText: ignoreNullish(validateColorWithoutTransparency),
    badgeWarningColorBorder: ignoreNullish(validateColorWithTransparency),
    badgeWarningColorBackground: ignoreNullish(validateColorWithoutTransparency),
    badgeWarningColorText: ignoreNullish(validateColorWithoutTransparency),
    badgeDangerColorBorder: ignoreNullish(validateColorWithTransparency),
    badgeDangerColorBackground: ignoreNullish(validateColorWithoutTransparency),
    badgeDangerColorText: ignoreNullish(validateColorWithoutTransparency),
    borderRadius: ignoreNullish(validateRadius),
    buttonBorderRadius: ignoreNullish(validateRadius),
    formBorderRadius: ignoreNullish(validateRadius),
    badgeBorderRadius: ignoreNullish(validateRadius),
    overlayBorderRadius: ignoreNullish(validateRadius),
    overlayZIndex: ignoreNullish(validateZIndex),
    overlayBackdropColor: ignoreNullish(validateColorWithTransparency),
    fontSizeBase: ignoreNullish(validateFontSizeBase),
    spacingUnit: ignoreNullish(validateSpacing),
    bodyMdFontSize: ignoreNullish(validateFontSize),
    bodyMdFontWeight: ignoreNullish(validateFontWeight),
    bodySmFontSize: ignoreNullish(validateFontSize),
    bodySmFontWeight: ignoreNullish(validateFontWeight),
    labelMdFontSize: ignoreNullish(validateFontSize),
    labelMdFontWeight: ignoreNullish(validateFontWeight),
    labelMdTextTransform: ignoreNullish(validateTextTransform),
    labelSmFontSize: ignoreNullish(validateFontSize),
    labelSmFontWeight: ignoreNullish(validateFontWeight),
    labelSmTextTransform: ignoreNullish(validateTextTransform),
    headingXlFontSize: ignoreNullish(validateFontSize),
    headingXlFontWeight: ignoreNullish(validateFontWeight),
    headingXlTextTransform: ignoreNullish(validateTextTransform),
    headingLgFontSize: ignoreNullish(validateFontSize),
    headingLgFontWeight: ignoreNullish(validateFontWeight),
    headingLgTextTransform: ignoreNullish(validateTextTransform),
    headingMdFontSize: ignoreNullish(validateFontSize),
    headingMdFontWeight: ignoreNullish(validateFontWeight),
    headingMdTextTransform: ignoreNullish(validateTextTransform),
    headingSmFontSize: ignoreNullish(validateFontSize),
    headingSmFontWeight: ignoreNullish(validateFontWeight),
    headingSmTextTransform: ignoreNullish(validateTextTransform),
    headingXsFontSize: ignoreNullish(validateFontSize),
    headingXsFontWeight: ignoreNullish(validateFontWeight),
    headingXsTextTransform: ignoreNullish(validateTextTransform)
};
export const validateOverlay = ignoreNullish(validateStringContents((value)=>{
    const isValidOverlay = value === 'dialog' || value === 'drawer';
    if (!isValidOverlay) {
        throw new Error(`The overlays value provided (${value}) must either be 'dialog' or 'drawer'.`);
    }
}));
export const validateAppearanceOptions = (logWarning)=>validateObject(logWarning, {
        overlays: validateOverlay,
        variables: validateObject(logWarning, _object_spread({}, validatorsForAppearanceOptions), true)
    }, true);

//# sourceMappingURL=validateAppearance.js.map